import React, { useRef, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { placeOrder, deleteFromCart, fetchFoods } from '../actions/actions';
import './CartDetails.scss';
import Receipt from './Receipt';

const CartDetails = ({
  cartItems,
  onClose,
  dispatchDeleteItem,
  dispatchPlaceOrder,
  dispatchFetchFoods,
}) => {
  const [quantities, setQuantities] = useState(cartItems.map(() => 1));
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const componentRef = useRef();
  const [orderResult, setOrderResult] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);

  useEffect(() => {
    setQuantities(cartItems.map(() => 1));
  }, [cartItems]);

  const calculateItemTotal = (item, index) => {
    const qty = quantities[index];
    let itemTotal = item.price * qty;

    if (item.addons && item.addons.length) {
      item.addons.forEach((addon) => {
        itemTotal += (addon.addOnPrice || 0) * qty;
      });
    }

    if (item.specialRequest && item.specialRequest.length) {
      item.specialRequest.forEach((special) => {
        itemTotal += (special.addOnPrice || 0) * qty;
      });
    }

    if (item.extraRequest && item.extraRequest.length) {
      item.extraRequest.forEach((extra) => {
        itemTotal += (extra.addOnPrice || 0) * qty;
      });
    }

    return itemTotal.toFixed(2);
  };

  const calculateTotal = () => {
    let total = 0;
    cartItems.forEach((item, index) => {
      total += parseFloat(calculateItemTotal(item, index));
    });
    return total.toFixed(2);
  };

  const handleDecrement = (index) => {
    setQuantities((prev) => {
      const updated = [...prev];
      updated[index] = Math.max(1, updated[index] - 1);
      return updated;
    });
  };

  const handleIncrement = (index) => {
    setQuantities((prev) => {
      const updated = [...prev];
      updated[index] += 1;
      return updated;
    });
  };

  const handlePlaceOrder = async () => {
    if (!cartItems.length) {
      alert('Cart is empty');
      return;
    }

    setLoading(true);

    try {
      const itemsWithQuantities = cartItems.map((item, index) => ({
        ...item,
        quantity: quantities[index],
      }));

      const response = await dispatchPlaceOrder(itemsWithQuantities, paymentMethod);

      console.log('Order response:', response);

      if (response?.success) {
        // keep the FULL raw response shape for Receipt
        setOrderResult(response.raw);
        setShowReceipt(true);
      } else {
        alert('Order failed');
      }
    } catch (error) {
      console.error('Error placing order:', error);
      alert(`Order failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = (index) => {
    dispatchDeleteItem(index);
  };

  const getAllSelectedRequests = (item) => {
    return [
      ...(item.addons || []).map((r) => ({ ...r, type: 'Addon' })),
      ...(item.specialRequest || []).map((r) => ({ ...r, type: 'Special' })),
      ...(item.extraRequest || []).map((r) => ({ ...r, type: 'Extra' })),
    ];
  };

  const handleReceiptClose = () => {
    setShowReceipt(false);
    setOrderResult(null);
    onClose();
  };

  if (showReceipt && orderResult) {
    return (
      <div className="cart-details-container">
        <Receipt orderData={orderResult} onClose={handleReceiptClose} />
      </div>
    );
  }

  return (
    <div className="cart-details-container">
      <h2>Cart Details</h2>

      <ul ref={componentRef}>
        {cartItems.length > 0 ? (
          cartItems.map((item, index) => {
            const allRequests = getAllSelectedRequests(item);
            const qty = quantities[index];

            return (
              <li key={index} className="cart-item">
                <img src={item.img} alt={item.title} className="cart-item-img" />

                <div className="cart-item-details">
                  <div className="cart-item-title">{item.title}</div>

                  {item.note && (
                    <div className="cart-item-note">
                      <span>Customer Note :</span> <span>{item.note}</span>
                    </div>
                  )}

                  <div className="cart-item-price">
                    <div className="price-row">
                      <span>Base:</span>
                      <span>
                        RM {item.price.toFixed(2)} × {qty} = RM {(item.price * qty).toFixed(2)}
                      </span>
                    </div>

                    {allRequests.map((req, idx) => {
                      const unitPrice = req.addOnPrice || 0;
                      const subtotal = unitPrice * qty;

                      return (
                        <div key={idx} className="price-row request-row">
                          <span>{req.name}:</span>
                          <span>
                            +RM {unitPrice.toFixed(2)} × {qty} = RM {subtotal.toFixed(2)}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="cart-item-quantity">
                    <button onClick={() => handleDecrement(index)}>-</button>
                    {qty}
                    <button onClick={() => handleIncrement(index)}>+</button>
                  </div>
                </div>

                <div className="cart-item-delete" onClick={() => handleDeleteItem(index)}>
                  &#10006;
                </div>
              </li>
            );
          })
        ) : (
          <li className="empty-cart">Your cart is empty</li>
        )}
      </ul>

      <div className="cart-total">Total: RM {calculateTotal()}</div>

      <div className="payment-method-section">
        <span className="section-label">Payment :</span>

        <label>
          <input
            type="radio"
            name="paymentMethod"
            value="cash"
            checked={paymentMethod === 'cash'}
            onChange={(e) => setPaymentMethod(e.target.value)}
          />
          Cash
        </label>

        <label>
          <input
            type="radio"
            name="paymentMethod"
            value="e-wallet"
            checked={paymentMethod === 'e-wallet'}
            onChange={(e) => setPaymentMethod(e.target.value)}
          />
          E-Wallet
        </label>

        <label>
          <input
            type="radio"
            name="paymentMethod"
            value="card"
            checked={paymentMethod === 'card'}
            onChange={(e) => setPaymentMethod(e.target.value)}
          />
          Debit & Credit
        </label>
      </div>

      <div className="button-group">
        <button onClick={handlePlaceOrder} disabled={loading}>
          {loading ? 'Placing Order...' : 'Place Order'}
        </button>
        <button onClick={onClose} disabled={loading}>
          Close
        </button>
      </div>

      {loading && <div className="loading-indicator">Loading...</div>}
    </div>
  );
};

const mapStateToProps = (state) => ({
  cartItems: state.cart,
});

const mapDispatchToProps = (dispatch) => ({
  dispatchDeleteItem: (index) => dispatch(deleteFromCart(index)),
  dispatchPlaceOrder: (cartItems, paymentMethod) =>
    dispatch(placeOrder(cartItems, paymentMethod)),
  dispatchFetchFoods: () => dispatch(fetchFoods()),
});

export default connect(mapStateToProps, mapDispatchToProps)(CartDetails);